<template>
  <div class="relative h-64">
    <picture class="absolute">
      <source srcset="../assets/default_bg_@3.webp" type="image/webp" />
      <img src="../assets/default_bg_@1.png" alt="Window" />
    </picture>
    <transition name="fade">
      <FreeCarouselLegacy
        v-if="!isCountriesLoading && hotCountries.length > 0"
        :key="carouselKey"
        :carousel-config="{
          gap: 0,
          peek: 0,
          type: 'carousel',
          startAt: activeSlideIndex,
        }"
        @onActiveSlideChange="onActiveSlideChange"
      >
        <template #default>
          <FreeCarouselItemLegacy
            v-for="(country, idx) in hotCountries"
            :key="country.destination.country.code"
          >
            <CountriesCarouselSlide
              :result="country"
              @click="
                onSlideClick({
                  index: idx,
                  origCityCode: country.origin.city.code,
                  destCountryCode: country.destination.country.code,
                })
              "
            />
          </FreeCarouselItemLegacy>
        </template>
        <template #bullets>
          <FreeBulletsLegacy class="absolute inset-x-0 bottom-2 mt-0">
            <template #default="{ index }">
              <span
                class="h-2 w-2 rounded-full duration-200"
                :class="[
                  index === activeSlideIndex
                    ? 'bg-static-white'
                    : 'bg-static-white/20',
                ]"
              />
            </template>
          </FreeBulletsLegacy>
        </template>
      </FreeCarouselLegacy>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import {
    FreeBulletsLegacy,
    FreeCarouselLegacy,
    FreeCarouselItemLegacy,
  } from 'freedom-shared';

  import CountriesCarouselSlide from './CountriesCarouselSlide.vue';
  import { useHotOffers } from '../model';

  const emit = defineEmits<{
    onActiveSlideChange: [
      destCountryCode: string,
      direction: '<' | '>',
      isInitialChange?: boolean,
    ];
    onSlideClick: [
      payload: { index: number; origCityCode: string; destCountryCode: string },
    ];
  }>();

  const {
    hotCountries,
    destinationCountryIndex,
    isCountriesLoading,
    destCountryCode,
    origCityCode,
  } = useHotOffers();

  const activeSlideIndex = ref(-1);

  // Create a key based on countries list to force carousel rerender when list changes
  const carouselKey = computed(() => {
    if (hotCountries.value.length === 0) return '';
    return `${origCityCode.value}-${hotCountries.value
      .map(c => c.destination.country.code)
      .join(',')}`;
  });

  function findIndexByDestCode(code: string | null): number {
    return hotCountries.value.findIndex(
      country => country.destination.country.code === code,
    );
  }

  watch(
    hotCountries,
    newVal => {
      if (newVal.length === 0) return;

      let initialIndex = -1;

      if (destCountryCode.value) {
        initialIndex = findIndexByDestCode(destCountryCode.value);
      }

      if (
        initialIndex === -1 &&
        destinationCountryIndex.value != null &&
        destinationCountryIndex.value >= 0
      ) {
        initialIndex = destinationCountryIndex.value;
      }

      if (initialIndex === -1) {
        initialIndex = 0;
      }

      activeSlideIndex.value = initialIndex;

      emit(
        'onActiveSlideChange',
        newVal[initialIndex]?.destination.country.code,
        '>',
        true,
      );
    },
    { immediate: true, deep: true },
  );

  function onActiveSlideChange(index: number, direction: '<' | '>') {
    activeSlideIndex.value = index;
    emit(
      'onActiveSlideChange',
      hotCountries.value[index]?.destination.country.code,
      direction,
    );
  }

  function onSlideClick({
    index,
    origCityCode,
    destCountryCode,
  }: {
    index: number;
    origCityCode: string;
    destCountryCode: string;
  }) {
    emit('onSlideClick', { index, origCityCode, destCountryCode });
  }
</script>
